/**
 * Пробой: выход цены за привычные границы.
 *
 * Пробой вниз меряется от самого низкого минимума (`breakOut` в kit),
 * а не от самого высокого — раньше зеркальная сторона срабатывала задолго
 * до настоящего пробоя.
 */
import { above, adx, bb, breakOut, close, ema, happened, ind, mirror, number, recently, rvol, spec, swingParams, weight, } from './kit.js';
export const BREAKOUT_TEMPLATES = [
    {
        id: 'breakout',
        name: 'Пробой полосы',
        icon: '💥',
        category: 'breakout',
        popular: true,
        hint: 'Цена вышла за пределы обычного размаха — с подтверждением объёмом.',
        steps: ['Цена пробила полосу Боллинджера', 'Объём выше обычного', 'Крупный тренд согласен'],
        keywords: ['пробой', 'боллинджер', 'breakout'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '4H',
            idea: 'Цена закрылась за полосой Боллинджера — вышла за обычный размах, и за ней пришёл объём.',
            why: 'Выход за обычный размах на объёме часто открывает новое движение: кто-то крупный начал действовать.',
            fails: 'Без объёма пробой полосы — обычное колебание, и цена возвращается внутрь.',
            tip: 'Порог силы 50 требует объёма: если снизить до нуля, сигналов станет втрое больше и хуже.',
        },
        build: (o) => spec(o, 'Пробой полосы', mirror((side) => ({
            context: [],
            trigger: side.cross(close, bb(side.sign > 0 ? 'upper' : 'lower')),
            confirmations: [weight('c1', above(rvol, number(1.5)), 60), weight('c2', side.beyond(close, ema(200)), 40)],
        })), 50),
    },
    {
        id: 'breakout-high',
        name: 'Пробой экстремума',
        icon: '💥',
        category: 'breakout',
        popular: true,
        hint: 'Цена ушла за крайнюю точку последних двадцати баров по ходу крупного тренда.',
        steps: ['Пробит экстремум 20 баров', 'Крупный тренд согласен', 'Тренд силён'],
        keywords: ['пробой', 'максимум', 'минимум', 'уровень'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '4H',
            idea: 'Цена обновила максимум (или минимум) последних 20 баров — вышла из недавнего коридора.',
            why: 'За экстремумом стоят стопы и отложенные заявки: их срабатывание разгоняет движение.',
            fails: 'В боковике экстремумы обновляются туда-обратно, и каждый пробой возвращается.',
            tip: 'Пробой по направлению EMA 200 работает заметно чаще, чем против неё.',
        },
        build: (o) => spec(o, 'Пробой экстремума', mirror((side) => ({
            context: [],
            trigger: breakOut(side, 20),
            confirmations: [weight('c1', side.beyond(close, ema(200)), 60), weight('c2', above(adx, number(20)), 40)],
        })), 50),
    },
    {
        id: 'range-break',
        name: 'Выход из сжатия',
        icon: '🧨',
        category: 'breakout',
        hint: 'Рынок сжался внутренними барами, затем вышел из этого сжатия.',
        steps: ['Были внутренние бары', 'Пробит экстремум 10 баров', 'Объём'],
        keywords: ['сжатие', 'inside bar', 'range'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '4H',
            idea: 'Несколько свечей подряд уместились внутри предыдущей — рынок замер. Затем цена вышла за границу этого затишья.',
            why: 'Внутренние бары — знак равновесия; выход из него показывает, кто победил.',
            fails: 'Выход без объёма часто оказывается ложным и тут же возвращается.',
            tip: 'Ставьте защиту за противоположной границей сжатия — она близко, и риск маленький.',
        },
        build: (o) => spec(o, 'Выход из сжатия', mirror((side) => ({
            context: [recently(ind('inside_bar', {}), 3)],
            trigger: breakOut(side, 10),
            confirmations: [weight('c1', above(rvol, number(1.4)), 100)],
        })), 0),
    },
    {
        id: 'false-breakout',
        name: 'Ложный пробой',
        icon: '🪤',
        category: 'breakout',
        hint: 'Цена зашла за уровень тенью и вернулась: пробой не состоялся, вход против него.',
        steps: ['Ликвидность снята тенью', 'Свеча закрылась обратно', 'Поглощение и объём'],
        keywords: ['ложный пробой', 'sweep', 'ловушка'],
        passport: {
            level: 'skilled',
            market: 'range',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '15m',
            idea: 'Цена кольнула тенью за экстремум — сработали чужие стопы — и закрылась обратно. Пробой был ловушкой.',
            why: 'Крупным участникам нужна ликвидность: они «собирают» стопы за уровнем и разворачивают цену.',
            fails: 'Если следующая свеча всё же закрывается за уровнем — это был не ложный пробой, а начало настоящего.',
            tip: 'Чем длиннее тень за уровнем и чем больше объём на ней — тем честнее ловушка.',
        },
        build: (o) => spec(o, 'Ложный пробой', mirror((side) => ({
            context: [],
            trigger: happened(ind('sweep', swingParams, side.liquidity)),
            confirmations: [
                weight('c1', happened(ind('engulfing', { factor: 1 }, side.way)), 40),
                weight('c2', above(rvol, number(1.3)), 60),
            ],
        })), 40),
    },
];
//# sourceMappingURL=breakout.js.map